import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsk } from '../components/Ask';
import type { CompanyRole, PersonRow } from '../lib/types';
import { ROLE_LABELS } from '../lib/types';
import { formatDate } from '../lib/dates';
import { usePaged } from '../components/ListPager';

interface PendingInvitation {
  id: string;
  email: string;
  role: CompanyRole;
  invitedBy: string | null;
  invitedAt: string;
}

/**
 * The customer's own people, on their own company page.
 *
 * WHY IT IS HERE AND NOT ON ROLES & ACCESS. That page is ours — it lists every account in the
 * group and is administrators only. A client admin needs one thing from it: adding somebody
 * from their own business. So they get that one thing, where their business already is.
 *
 * THE API IS THE RULE, NOT THIS CARD. Everyone invited from here arrives as a client, in this
 * company, never as an administrator — enforced in `invitations.service`, which refuses
 * anything else whatever this screen sends.
 *
 * NOBODY IS ADDED, THEY ARE ASKED. The form takes an email and the person accepts for
 * themselves, which is not politeness: attaching whoever an address belongs to would let
 * anybody with this card discover, one guess at a time, which addresses already have a Hub
 * account — our own staff, and other customers' people. So the answer is always the same
 * "waiting for them", and nothing here ever reveals what was found.
 */
export function CompanyPeopleCard({
  companyId,
  companyName,
  isAdmin,
}: {
  companyId: string;
  companyName: string;
  /** An administrator sees the same card, with the roles they alone can grant already set. */
  isAdmin: boolean;
}) {
  const ask = useAsk();
  const [people, setPeople] = useState<PersonRow[] | null>(null);
  const peoplePage = usePaged(people ?? [], 'companyPeople');
  const [invited, setInvited] = useState<PendingInvitation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const load = useCallback(async () => {
    try {
      const [rows, waiting] = await Promise.all([
        api.get<PersonRow[]>('/people'),
        api.get<PendingInvitation[]>(`/companies/${companyId}/invitations`),
      ]);
      /* An administrator's list is everybody, so it is narrowed to this company here. */
      setPeople(rows.filter((person) => person.companies.some((company) => company.id === companyId)));
      setInvited(waiting);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the people on this company');
      setPeople([]);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function send() {
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);

    try {
      const result = await api.post<{ invited: boolean; email: string }>(
        `/companies/${companyId}/invitations`,
        { email: email.trim(), name: name.trim() },
      );

      /* The same words whatever was found on the other side. That is the point. */
      setSent(result.email);
      setName('');
      setEmail('');
      setJobTitle('');
      setAdding(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that invitation');
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(invitation: PendingInvitation) {
    setBusy(true);
    try {
      await api.del(`/invitations/${invitation.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not withdraw that invitation');
    } finally {
      setBusy(false);
    }
  }

  async function remove(person: PersonRow) {
    const sure = await ask.confirm({
      title: `Remove ${person.name} from ${companyName}?`,
      body: 'Their account stays, and they keep any other company they belong to. They simply lose this one.',
      confirmLabel: 'Remove them',
      danger: true,
    });
    if (!sure) return;

    setBusy(true);
    try {
      await api.del(`/people/${person.id}/roles/${companyId}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove them');
    } finally {
      setBusy(false);
    }
  }

  const roleOn = (person: PersonRow): CompanyRole | null =>
    person.companies.find((company) => company.id === companyId)?.role ?? null;

  return (
    <div className="card mb-2">
      <div className="card-head">
        <h2>People</h2>
        {!adding && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            Invite someone
          </button>
        )}
      </div>

      <div className="card-pad">
        {error && <div className="callout mb-2">{error}</div>}

        {sent && (
          <div className="callout mb-2">
            We have emailed <strong>{sent}</strong>. They join {companyName} when they accept — until then
            nothing of yours is shared with them.
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              style={{ marginLeft: 10 }}
              onClick={() => setSent(null)}
            >
              Done
            </button>
          </div>
        )}

        {adding && (
          <div className="stack-sm mb-2">
            <div className="row wrap" style={{ gap: 8 }}>
              <label className="field grow" style={{ marginBottom: 0, minWidth: 180 }}>
                {/* Used only if that address turns out to have no account; an existing one keeps its own. */}
                <span className="field-label">Name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Thandi Mokoena" />
              </label>
              <label className="field grow" style={{ marginBottom: 0, minWidth: 200 }}>
                <span className="field-label">Work email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="thandi@theircompany.co.za"
                />
              </label>
              <label className="field" style={{ marginBottom: 0, minWidth: 160 }}>
                <span className="field-label">Job title</span>
                <input value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} placeholder="Optional" />
              </label>
            </div>

            <div className="row" style={{ gap: 7 }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void send()}>
                {busy ? 'Sending…' : 'Send the invitation'}
              </button>
              <button type="button" className="btn btn-quiet btn-sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
              {/* Said before they press it, so the role is never a surprise afterwards. */}
              <span className="dim" style={{ fontSize: 11 }}>
                They are emailed an invitation. Accepting joins {companyName} as a client: they see
                everything of yours and can raise a request.
              </span>
            </div>
          </div>
        )}

        {/* Asked and not answered. Shown so an invitation is never quietly forgotten. */}
        {invited.length > 0 && (
          <div className="stack-sm mb-2">
            {invited.map((invitation) => (
              <div key={invitation.id} className="row-between">
                <span className="grow" style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{invitation.email}</span>
                  <span className="doc-meta">
                    Invited {formatDate(invitation.invitedAt)}
                    {invitation.invitedBy ? ` by ${invitation.invitedBy}` : ''}
                  </span>
                </span>
                <span className="badge badge-warn">Waiting for them</span>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={busy}
                  onClick={() => void withdraw(invitation)}
                >
                  Withdraw
                </button>
              </div>
            ))}
          </div>
        )}

        {people === null ? (
          <span className="dim">Loading…</span>
        ) : people.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            Nobody has access to {companyName} yet.
          </p>
        ) : (
          <div className="stack-sm">
            {peoplePage.visible.map((person) => {
              const role = roleOn(person);
              return (
                <div key={person.id} className="row-between">
                  <span className="grow" style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{person.name}</span>
                    <span className="doc-meta">
                      {person.email}
                      {person.jobTitle ? ` · ${person.jobTitle}` : ''}
                      {person.isActive ? '' : ' · deactivated'}
                    </span>
                  </span>

                  <span className="badge">{role ? ROLE_LABELS[role] : '—'}</span>

                  {/*
                    A client admin takes off their own clients and nobody else. Another client
                    admin is shown with the button DISABLED rather than missing: they are a
                    real person on this account and hiding them makes the list look wrong, but
                    only Lucentra moves them, and the API refuses it either way.
                  */}
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy || !(isAdmin || role === 'client')}
                    title={
                      isAdmin || role === 'client'
                        ? undefined
                        : `${person.name}'s access here is set by Lucentra`
                    }
                    onClick={() => void remove(person)}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {people && people.length > 0 && peoplePage.pager && <div style={{ marginTop: 12 }}>{peoplePage.pager}</div>}
      </div>
    </div>
  );
}
